<template>
  <div>
    <v-btn
      v-can="'ROLE_Funcionario_Update'"
      :loading="abrindo"
      class="
        ma-1
        font-weight-bold
        text-none
        btn-primary
        bg-gradient-primary
        py-3
        px-6
        ms-3
      "
      height="39"
      @click="abrir"
    >
      <v-icon left>mdi-account-edit-outline</v-icon>
      Atualizar em massa
    </v-btn>

    <v-dialog v-model="dialog" max-width="1120" persistent scrollable>
      <v-card class="bulk-update-card">
        <v-card-title class="bulk-update-header pa-5">
          <div>
            <div class="text-h6 font-weight-bold">Atualização em massa</div>
            <div class="text-body-2 bulk-update-header__subtitle">
              Um fluxo seguro para alterar vários funcionários por Excel
            </div>
          </div>
          <v-spacer />
          <v-btn icon :disabled="ocupado" aria-label="Fechar" @click="fechar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-stepper
            v-model="passo"
            flat
            alt-labels
            class="bulk-update-stepper"
          >
            <v-stepper-header>
              <v-stepper-step :complete="passo > 1" step="1"
                >Preparar</v-stepper-step
              >
              <v-divider />
              <v-stepper-step :complete="passo > 2" step="2"
                >Validar e aplicar</v-stepper-step
              >
              <v-divider />
              <v-stepper-step step="3">Concluído</v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content step="1" class="pa-5">
                <v-alert
                  text
                  color="primary"
                  icon="mdi-shield-check-outline"
                  class="mb-5"
                >
                  Escolha quem será incluído e quais campos poderão mudar. O
                  arquivo virá com os valores atuais, listas de opções e
                  proteção contra dados desatualizados.
                </v-alert>

                <div class="bulk-update-section-title">
                  <span>1. Filtre os funcionários</span>
                  <div
                    v-if="quantidade !== null"
                    class="d-flex align-center flex-wrap justify-end"
                  >
                    <v-chip
                      small
                      :color="quantidade ? 'primary' : 'warning'"
                      outlined
                    >
                      {{ quantidade }} encontrado{{
                        quantidade === 1 ? "" : "s"
                      }}
                    </v-chip>
                    <v-btn
                      v-if="selecaoVerificada && quantidade > 0"
                      small
                      text
                      color="primary"
                      class="ml-2 text-none"
                      :loading="carregandoFuncionarios"
                      @click="alternarFuncionariosEncontrados"
                    >
                      <v-icon left small>{{
                        mostrarFuncionarios
                          ? "mdi-eye-off-outline"
                          : "mdi-eye-outline"
                      }}</v-icon>
                      {{
                        mostrarFuncionarios
                          ? "Ocultar funcionários"
                          : "Ver funcionários"
                      }}
                    </v-btn>
                  </div>
                </div>

                <v-row dense>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.trim="filtro.nome"
                      label="Nome contém"
                      prepend-inner-icon="mdi-account-search-outline"
                      outlined
                      dense
                      clearable
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.trim="filtro.matricula"
                      label="Matrícula exata"
                      prepend-inner-icon="mdi-card-account-details-outline"
                      outlined
                      dense
                      clearable
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="filtro.dsStatusFuncionario"
                      :items="statusOptions"
                      label="Status"
                      prepend-inner-icon="mdi-list-status"
                      outlined
                      dense
                      clearable
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-btn
                      block
                      color="primary"
                      outlined
                      height="40"
                      :loading="consultandoQuantidade"
                      @click="verificarSelecao"
                    >
                      <v-icon left>mdi-account-group-outline</v-icon>
                      Verificar seleção
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.trim="filtro.cliente"
                      :label="
                        isOrgaoPublico ? 'Lotação contém' : 'Cliente contém'
                      "
                      outlined
                      dense
                      clearable
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.trim="filtro.postoTrabalho"
                      label="Local de trabalho contém"
                      outlined
                      dense
                      clearable
                      hide-details
                    />
                  </v-col>
                </v-row>

                <v-alert
                  v-if="quantidade > limiteFuncionarios"
                  dense
                  text
                  type="warning"
                  class="mt-4 mb-0"
                >
                  A seleção excede o limite seguro de
                  {{ limiteFuncionarios.toLocaleString("pt-BR") }} funcionários
                  por arquivo. Refine os filtros para continuar.
                </v-alert>

                <v-card
                  v-if="mostrarFuncionarios"
                  outlined
                  class="bulk-update-preview mt-4"
                >
                  <v-card-title class="py-3 px-4">
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">
                        Funcionários encontrados
                      </div>
                      <div class="text-caption grey--text text--darken-1">
                        Confira a seleção que será incluída no modelo Excel.
                      </div>
                    </div>
                    <v-spacer />
                    <v-chip small color="primary" outlined>
                      {{ quantidade }} registro{{ quantidade === 1 ? "" : "s" }}
                    </v-chip>
                  </v-card-title>
                  <v-divider />
                  <v-data-table
                    :headers="headersFuncionariosEncontrados"
                    :items="funcionariosEncontrados"
                    :loading="carregandoFuncionarios"
                    :server-items-length="quantidade || 0"
                    :page="paginaFuncionarios"
                    :items-per-page="itensPorPaginaFuncionarios"
                    :footer-props="{
                      itemsPerPageOptions: [5, 10, 20],
                      itemsPerPageText: 'Registros por página',
                    }"
                    dense
                    class="elevation-0"
                    loading-text="Carregando funcionários..."
                    no-data-text="Nenhum funcionário encontrado com estes filtros."
                    @update:options="opcoesFuncionariosAlteradas"
                  >
                    <template #[`item.matricula`]="{ item }">
                      {{ item.matricula || "—" }}
                    </template>
                    <template #[`item.nomeSocial`]="{ item }">
                      {{ item.nomeSocial || "—" }}
                    </template>
                    <template #[`item.cargo`]="{ item }">
                      {{ item.cargo || "—" }}
                    </template>
                    <template #[`item.cliente`]="{ item }">
                      {{ item.cliente || "—" }}
                    </template>
                    <template #[`item.postoTrabalho`]="{ item }">
                      {{ item.postoTrabalho || "—" }}
                    </template>
                    <template #[`item.status`]="{ item }">
                      <v-chip
                        x-small
                        outlined
                        :color="corStatusFuncionario(item.status)"
                      >
                        {{ descricaoStatusFuncionario(item.status) }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card>

                <v-divider class="my-6" />

                <div class="bulk-update-section-title">
                  <span>2. Escolha os campos editáveis</span>
                  <span class="text-caption grey--text text--darken-1">
                    {{ camposSelecionados.length }} selecionado{{
                      camposSelecionados.length === 1 ? "" : "s"
                    }}
                  </span>
                </div>

                <v-row dense class="mb-3">
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="buscaCampo"
                      label="Buscar campo"
                      prepend-inner-icon="mdi-magnify"
                      outlined
                      dense
                      clearable
                      hide-details
                    />
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-btn
                      block
                      text
                      color="primary"
                      height="40"
                      @click="selecionarCamposVisiveis"
                    >
                      Selecionar visíveis
                    </v-btn>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-btn
                      block
                      text
                      color="grey darken-1"
                      height="40"
                      @click="camposSelecionados = []"
                    >
                      Limpar
                    </v-btn>
                  </v-col>
                </v-row>

                <v-skeleton-loader
                  v-if="carregandoCampos"
                  type="list-item-three-line@4"
                />
                <div v-else class="bulk-update-fields">
                  <v-card
                    v-for="grupo in gruposCampos"
                    :key="grupo.nome"
                    outlined
                    class="bulk-update-field-group mb-3"
                  >
                    <v-card-title class="py-3 text-subtitle-2">
                      {{ grupo.nome }}
                      <v-spacer />
                      <v-chip x-small outlined
                        >{{ quantidadeSelecionadaGrupo(grupo) }}/{{
                          grupo.campos.length
                        }}</v-chip
                      >
                    </v-card-title>
                    <v-divider />
                    <v-row no-gutters class="pa-2">
                      <v-col
                        v-for="campo in grupo.campos"
                        :key="campo.chave"
                        cols="12"
                        md="6"
                      >
                        <div class="bulk-update-field-option">
                          <v-checkbox
                            v-model="camposSelecionados"
                            :value="campo.chave"
                            hide-details
                            class="mt-0"
                          >
                            <template #label>
                              <div>
                                <div class="text-body-2 font-weight-medium">
                                  {{ campo.descricao }}
                                  <v-chip
                                    v-if="campo.obrigatorio"
                                    x-small
                                    color="warning"
                                    outlined
                                    class="ml-1"
                                  >
                                    não pode ficar vazio
                                  </v-chip>
                                </div>
                                <div
                                  class="text-caption grey--text text--darken-1"
                                >
                                  {{ descricaoTipo(campo) }}
                                </div>
                              </div>
                            </template>
                          </v-checkbox>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                  <v-alert v-if="!gruposCampos.length" text type="info"
                    >Nenhum campo corresponde à busca.</v-alert
                  >
                </div>
              </v-stepper-content>

              <v-stepper-content step="2" class="pa-5">
                <v-alert
                  text
                  color="success"
                  icon="mdi-file-excel-outline"
                  class="mb-5"
                >
                  O modelo foi baixado. Edite as colunas azuis, salve o arquivo
                  e envie-o abaixo. Antes de aplicar, mostraremos exatamente
                  quantos funcionários e valores serão alterados.
                </v-alert>

                <v-file-input
                  v-model="arquivo"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  label="Selecione o modelo preenchido (.xlsx)"
                  prepend-icon="mdi-file-excel"
                  outlined
                  show-size
                  :disabled="aplicando"
                  :loading="validando"
                  @change="arquivoAlterado"
                />

                <v-progress-linear
                  v-if="validando"
                  indeterminate
                  color="primary"
                  class="mb-4"
                />

                <template v-if="resultadoValidacao">
                  <v-alert
                    v-if="resultadoValidacao.valido"
                    text
                    type="success"
                    icon="mdi-check-decagram-outline"
                  >
                    Arquivo validado:
                    {{ resultadoValidacao.funcionariosComAlteracao }}
                    funcionário(s) e
                    {{ resultadoValidacao.totalAlteracoes }} alteração(ões).
                    Nenhum dado foi gravado ainda.
                  </v-alert>
                  <v-alert
                    v-else
                    text
                    type="error"
                    icon="mdi-alert-circle-outline"
                  >
                    Encontramos
                    {{ resultadoValidacao.erros.length }} inconsistência(s).
                    Corrija o arquivo e envie-o novamente. Nenhuma alteração foi
                    gravada.
                  </v-alert>

                  <v-card
                    v-if="
                      resultadoValidacao.erros &&
                      resultadoValidacao.erros.length
                    "
                    outlined
                    class="mb-4"
                  >
                    <v-card-title class="text-subtitle-2 py-3"
                      >O que precisa ser corrigido</v-card-title
                    >
                    <v-data-table
                      :headers="headersErros"
                      :items="resultadoValidacao.erros"
                      :items-per-page="10"
                      dense
                      class="elevation-0"
                    >
                      <template #[`item.linha`]="{ item }">{{
                        item.linha || "—"
                      }}</template>
                    </v-data-table>
                  </v-card>

                  <v-expansion-panels
                    v-if="
                      resultadoValidacao.valido &&
                      resultadoValidacao.alteracoes.length
                    "
                    flat
                    class="mb-4"
                  >
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        Conferir alterações
                        <span class="ml-2 text-caption grey--text">
                          (mostrando
                          {{ resultadoValidacao.alteracoes.length }} de
                          {{ resultadoValidacao.totalAlteracoes }})
                        </span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-data-table
                          :headers="headersAlteracoes"
                          :items="resultadoValidacao.alteracoes"
                          :items-per-page="10"
                          dense
                          class="elevation-0"
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <v-alert
                    v-if="
                      resultadoValidacao.valido &&
                      !resultadoValidacao.totalAlteracoes
                    "
                    text
                    type="info"
                  >
                    O arquivo está válido, mas não contém valores diferentes dos
                    dados atuais.
                  </v-alert>
                </template>
              </v-stepper-content>

              <v-stepper-content step="3" class="pa-8 text-center">
                <v-icon color="success" size="72"
                  >mdi-check-circle-outline</v-icon
                >
                <div class="text-h5 font-weight-bold mt-4">
                  Atualização concluída
                </div>
                <div class="text-body-1 grey--text text--darken-1 mt-2">
                  {{
                    resultadoAplicacao
                      ? resultadoAplicacao.funcionariosAtualizados
                      : 0
                  }}
                  funcionário(s) atualizado(s) com sucesso.
                </div>
                <v-btn
                  class="
                    mt-6
                    font-weight-bold
                    text-none
                    btn-primary
                    bg-gradient-primary
                    px-6
                  "
                  height="39"
                  @click="fechar"
                >
                  Fechar e atualizar listagem
                </v-btn>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>

        <v-divider v-if="passo < 3" />
        <v-card-actions v-if="passo < 3" class="crud-form-actions">
          <v-btn
            class="
              ma-1
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              px-6
            "
            height="39"
            :loading="ocupado"
            @click="passo === 1 ? fechar() : voltarConfiguracao()"
          >
            <v-icon left>{{
              passo === 1 ? "mdi-close" : "mdi-arrow-left"
            }}</v-icon>
            {{ $t(passo === 1 ? "Cancelar" : "Voltar") }}
          </v-btn>
          <v-btn
            v-if="passo === 1"
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-6
              ms-3
            "
            height="39"
            :loading="gerando"
            :disabled="
              !camposSelecionados.length ||
              quantidade === 0 ||
              !selecaoVerificada ||
              carregandoCampos ||
              consultandoQuantidade ||
              carregandoFuncionarios ||
              quantidade > limiteFuncionarios
            "
            @click="gerarModelo"
          >
            <v-icon left>mdi-file-excel-outline</v-icon>
            Gerar modelo
          </v-btn>
          <v-btn
            v-else
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-6
              ms-3
            "
            height="39"
            :loading="aplicando"
            :disabled="
              !resultadoValidacao ||
              !resultadoValidacao.valido ||
              !resultadoValidacao.totalAlteracoes ||
              validando
            "
            @click="confirmarAplicacao"
          >
            <v-icon left>mdi-check-circle-outline</v-icon>
            Aplicar atualizações
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FuncionarioDataService from "@/services/pessoal/FuncionarioDataService";

export default {
  name: "AtualizacaoFuncionarioLote",
  data() {
    return {
      dialog: false,
      passo: 1,
      abrindo: false,
      carregandoCampos: false,
      consultandoQuantidade: false,
      carregandoFuncionarios: false,
      gerando: false,
      validando: false,
      aplicando: false,
      filtro: this.novoFiltro(),
      quantidade: null,
      selecaoVerificada: false,
      mostrarFuncionarios: false,
      funcionariosEncontrados: [],
      paginaFuncionarios: 1,
      itensPorPaginaFuncionarios: 10,
      consultaSelecaoId: 0,
      campos: [],
      camposSelecionados: [],
      buscaCampo: "",
      arquivo: null,
      resultadoValidacao: null,
      resultadoAplicacao: null,
      limiteFuncionarios: 10000,
      statusOptions: [
        { text: "Todos", value: null },
        { text: "Ativo", value: "ATIVO" },
        { text: "Férias", value: "FERIAS" },
        { text: "Afastado", value: "AFASTADO" },
        { text: "Desligado", value: "DESLIGADO" },
      ],
      headersErros: [
        { text: "Linha", value: "linha", width: 76 },
        { text: "Matrícula", value: "matricula", width: 120 },
        { text: "Campo", value: "campo", width: 190 },
        { text: "Como corrigir", value: "mensagem", sortable: false },
      ],
      headersAlteracoes: [
        { text: "Matrícula", value: "matricula", width: 110 },
        { text: "Funcionário", value: "nomeSocial", width: 190 },
        { text: "Campo", value: "campo", width: 180 },
        { text: "Valor atual", value: "valorAtual" },
        { text: "Novo valor", value: "novoValor" },
      ],
    };
  },
  computed: {
    ocupado() {
      return (
        this.abrindo ||
        this.carregandoCampos ||
        this.consultandoQuantidade ||
        this.carregandoFuncionarios ||
        this.gerando ||
        this.validando ||
        this.aplicando
      );
    },
    isOrgaoPublico() {
      return this.$store?.state?.inquilino?.tipoContratacao === "ORGAOPUBLICO";
    },
    headersFuncionariosEncontrados() {
      return [
        { text: "Matrícula", value: "matricula", sortable: false },
        { text: "Nome", value: "nomeSocial", sortable: false },
        { text: "Cargo", value: "cargo", sortable: false },
        {
          text: this.isOrgaoPublico ? "Lotação" : "Cliente",
          value: "cliente",
          sortable: false,
        },
        {
          text: this.isOrgaoPublico ? "Local de trabalho" : "Posto de trabalho",
          value: "postoTrabalho",
          sortable: false,
        },
        { text: "Status", value: "status", sortable: false },
      ];
    },
    camposFiltrados() {
      const termo = this.normalizar(this.buscaCampo);
      if (!termo) return this.campos;
      return this.campos.filter(
        (campo) =>
          this.normalizar(campo.descricao).includes(termo) ||
          this.normalizar(campo.grupo).includes(termo)
      );
    },
    gruposCampos() {
      const grupos = new Map();
      this.camposFiltrados.forEach((campo) => {
        if (!grupos.has(campo.grupo)) grupos.set(campo.grupo, []);
        grupos.get(campo.grupo).push(campo);
      });
      return Array.from(grupos.entries()).map(([nome, campos]) => ({
        nome,
        campos,
      }));
    },
  },
  watch: {
    filtro: {
      deep: true,
      handler() {
        this.consultaSelecaoId += 1;
        this.consultandoQuantidade = false;
        this.carregandoFuncionarios = false;
        this.quantidade = null;
        this.selecaoVerificada = false;
        this.mostrarFuncionarios = false;
        this.funcionariosEncontrados = [];
        this.paginaFuncionarios = 1;
      },
    },
  },
  methods: {
    novoFiltro() {
      return {
        nome: null,
        matricula: null,
        cliente: null,
        postoTrabalho: null,
        dsStatusFuncionario: "ATIVO",
      };
    },
    async abrir() {
      this.resetar();
      this.dialog = true;
      this.abrindo = true;
      try {
        await this.carregarCampos();
      } finally {
        this.abrindo = false;
      }
    },
    fechar() {
      if (this.ocupado) return;
      const concluiu = this.passo === 3;
      this.dialog = false;
      if (concluiu) this.$emit("concluido", this.resultadoAplicacao);
      this.$nextTick(this.resetar);
    },
    resetar() {
      this.passo = 1;
      this.filtro = this.novoFiltro();
      this.consultaSelecaoId += 1;
      this.quantidade = null;
      this.selecaoVerificada = false;
      this.mostrarFuncionarios = false;
      this.carregandoFuncionarios = false;
      this.funcionariosEncontrados = [];
      this.paginaFuncionarios = 1;
      this.itensPorPaginaFuncionarios = 10;
      this.camposSelecionados = [];
      this.buscaCampo = "";
      this.arquivo = null;
      this.resultadoValidacao = null;
      this.resultadoAplicacao = null;
    },
    async carregarCampos() {
      this.carregandoCampos = true;
      try {
        const response = await FuncionarioDataService.getUpdateImportFields();
        this.campos = response.data || [];
      } catch (error) {
        await this.mostrarErro(
          error,
          "Não foi possível carregar os campos disponíveis."
        );
        this.dialog = false;
      } finally {
        this.carregandoCampos = false;
      }
    },
    payloadFiltro() {
      return {
        matricula: this.filtro.matricula || null,
        nome: this.filtro.nome || null,
        cliente: this.filtro.cliente || null,
        postoTrabalho: this.filtro.postoTrabalho || null,
        dsStatusFuncionario: this.filtro.dsStatusFuncionario || null,
      };
    },
    async consultarQuantidade() {
      const consultaId = ++this.consultaSelecaoId;
      this.consultandoQuantidade = true;
      try {
        const response =
          await FuncionarioDataService.countUpdateImportEmployees(
            this.payloadFiltro()
          );
        if (consultaId !== this.consultaSelecaoId) return null;
        this.quantidade = Number(response.data || 0);
        return this.quantidade;
      } catch (error) {
        if (consultaId !== this.consultaSelecaoId) return null;
        this.quantidade = null;
        await this.mostrarErro(
          error,
          "Não foi possível verificar os funcionários selecionados."
        );
        return null;
      } finally {
        if (consultaId === this.consultaSelecaoId) {
          this.consultandoQuantidade = false;
        }
      }
    },
    async verificarSelecao() {
      this.paginaFuncionarios = 1;
      this.mostrarFuncionarios = false;
      this.funcionariosEncontrados = [];
      this.selecaoVerificada = false;
      const quantidade = await this.consultarQuantidade();
      if (quantidade !== null) {
        this.selecaoVerificada = true;
      }
    },
    async alternarFuncionariosEncontrados() {
      if (this.mostrarFuncionarios) {
        this.mostrarFuncionarios = false;
        return;
      }
      this.paginaFuncionarios = 1;
      this.mostrarFuncionarios = true;
      await this.carregarFuncionariosEncontrados();
    },
    async carregarFuncionariosEncontrados() {
      const consultaId = ++this.consultaSelecaoId;
      this.carregandoFuncionarios = true;
      try {
        const response = await FuncionarioDataService.getUpdateImportEmployees(
          this.payloadFiltro(),
          {
            page: this.paginaFuncionarios - 1,
            linesPerPage: this.itensPorPaginaFuncionarios,
          }
        );
        if (consultaId !== this.consultaSelecaoId) return;
        const pagina = response.data || {};
        this.funcionariosEncontrados = Array.isArray(pagina.content)
          ? pagina.content
          : [];
        this.quantidade = Number(
          pagina.totalElements || this.funcionariosEncontrados.length
        );
        this.selecaoVerificada = true;
      } catch (error) {
        if (consultaId !== this.consultaSelecaoId) return;
        this.mostrarFuncionarios = false;
        this.funcionariosEncontrados = [];
        await this.mostrarErro(
          error,
          "Não foi possível carregar os funcionários encontrados."
        );
      } finally {
        if (consultaId === this.consultaSelecaoId) {
          this.carregandoFuncionarios = false;
        }
      }
    },
    opcoesFuncionariosAlteradas(opcoes) {
      const novaPagina = Number(opcoes?.page) || 1;
      const novosItens = Number(opcoes?.itemsPerPage) || 10;
      const mudou =
        novaPagina !== this.paginaFuncionarios ||
        novosItens !== this.itensPorPaginaFuncionarios;
      this.paginaFuncionarios = novaPagina;
      this.itensPorPaginaFuncionarios = novosItens;
      if (this.mostrarFuncionarios && mudou) {
        this.carregarFuncionariosEncontrados();
      }
    },
    descricaoStatusFuncionario(status) {
      const opcao = this.statusOptions.find((item) => item.value === status);
      return opcao ? opcao.text : status || "—";
    },
    corStatusFuncionario(status) {
      return {
        ATIVO: "success",
        FERIAS: "info",
        AFASTADO: "warning",
        DESLIGADO: "grey darken-1",
      }[status];
    },
    selecionarCamposVisiveis() {
      const selecionados = new Set(this.camposSelecionados);
      this.camposFiltrados.forEach((campo) => selecionados.add(campo.chave));
      this.camposSelecionados = Array.from(selecionados);
    },
    quantidadeSelecionadaGrupo(grupo) {
      const selecionados = new Set(this.camposSelecionados);
      return grupo.campos.filter((campo) => selecionados.has(campo.chave))
        .length;
    },
    descricaoTipo(campo) {
      const nomes = {
        TEXTO: "Texto livre",
        DATA: "Data (dd/mm/aaaa)",
        DECIMAL: "Valor numérico",
        BOOLEANO: "Opções: SIM ou NÃO",
        CIDADE: "Seleção de cidade",
        ESTADO: "Seleção de estado",
      };
      if (campo.tipo !== "ENUM") return nomes[campo.tipo] || campo.tipo;
      const opcoes = (campo.opcoes || []).map((item) => item.descricao);
      if (!opcoes.length) return "Lista de opções";
      const exibidas = opcoes.slice(0, 4).join(", ");
      return `Opções: ${exibidas}${
        opcoes.length > 4 ? ` e mais ${opcoes.length - 4}` : ""
      }`;
    },
    async gerarModelo() {
      if (!this.camposSelecionados.length || !this.selecaoVerificada) return;
      if (
        this.camposSelecionados.includes("status") &&
        !this.camposSelecionados.includes("dtDesligamento")
      ) {
        this.camposSelecionados = [
          ...this.camposSelecionados,
          "dtDesligamento",
        ];
      }
      if (this.quantidade === null) {
        const quantidade = await this.consultarQuantidade();
        if (quantidade === null || quantidade === 0) return;
      }
      if (this.quantidade > this.limiteFuncionarios) return;
      this.gerando = true;
      try {
        const response =
          await FuncionarioDataService.generateUpdateImportTemplate({
            filtro: this.payloadFiltro(),
            campos: this.camposSelecionados,
          });
        this.baixar(response.data, "funcionarios-atualizacao.xlsx");
        this.passo = 2;
      } catch (error) {
        await this.mostrarErro(error, "Não foi possível gerar o modelo Excel.");
      } finally {
        this.gerando = false;
      }
    },
    async arquivoAlterado(file) {
      this.resultadoValidacao = null;
      if (!file) return;
      const nome = String(file.name || "").toLowerCase();
      if (!nome.endsWith(".xlsx")) {
        this.arquivo = null;
        await this.$swal.fire(
          "Arquivo inválido",
          "Selecione o .xlsx gerado pelo Cajupe.",
          "warning"
        );
        return;
      }
      await this.validarArquivo();
    },
    async validarArquivo() {
      if (!this.arquivo) return;
      this.validando = true;
      try {
        const response = await FuncionarioDataService.validateUpdateImport(
          this.arquivo
        );
        this.resultadoValidacao = response.data;
      } catch (error) {
        this.resultadoValidacao = null;
        await this.mostrarErro(error, "Não foi possível validar o arquivo.");
      } finally {
        this.validando = false;
      }
    },
    async confirmarAplicacao() {
      const total = this.resultadoValidacao?.funcionariosComAlteracao || 0;
      const confirmacao = await this.$swal.fire({
        title: "Aplicar atualização?",
        text: `${total} funcionário(s) serão alterados. A planilha será validada novamente antes da gravação.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim, aplicar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmacao.isConfirmed) return;
      await this.aplicarArquivo();
    },
    async aplicarArquivo() {
      this.aplicando = true;
      try {
        const response = await FuncionarioDataService.applyUpdateImport(
          this.arquivo
        );
        if (!response.data?.valido) {
          this.resultadoValidacao = response.data;
          await this.$swal.fire(
            "O arquivo precisa ser revisto",
            "Os dados mudaram ou surgiu uma inconsistência desde a validação. Nada foi gravado.",
            "warning"
          );
          return;
        }
        this.resultadoAplicacao = response.data;
        this.passo = 3;
      } catch (error) {
        await this.mostrarErro(
          error,
          "A atualização não foi aplicada. Nenhum dado foi alterado."
        );
      } finally {
        this.aplicando = false;
      }
    },
    voltarConfiguracao() {
      if (this.ocupado) return;
      this.passo = 1;
      this.arquivo = null;
      this.resultadoValidacao = null;
    },
    baixar(content, fileName) {
      const url = URL.createObjectURL(new Blob([content]));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async mostrarErro(error, fallback) {
      let data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          data = JSON.parse(await data.text());
        } catch (_) {
          data = null;
        }
      }
      const detalhe =
        data?.message ||
        data?.errors?.[0]?.message ||
        error?.message ||
        fallback;
      await this.$swal.fire(
        "Não foi possível continuar",
        detalhe || fallback,
        "error"
      );
    },
    normalizar(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
  },
};
</script>

<style scoped>
.bulk-update-card {
  border-radius: 16px !important;
}

.bulk-update-header {
  background: #f8fafc;
  border-bottom: 1px solid #e7eaf0;
}

.bulk-update-header__subtitle {
  color: #667085;
}

.bulk-update-stepper {
  box-shadow: none !important;
}

.bulk-update-section-title {
  align-items: center;
  color: #344054;
  display: flex;
  font-size: 0.95rem;
  font-weight: 700;
  justify-content: space-between;
  margin-bottom: 14px;
}

.bulk-update-fields {
  max-height: 380px;
  overflow-y: auto;
  padding-right: 4px;
}

.bulk-update-field-group {
  border-color: #e4e7ec !important;
  border-radius: 10px !important;
}

.bulk-update-field-option {
  border-radius: 8px;
  min-height: 68px;
  padding: 10px 12px;
}

.bulk-update-field-option:hover {
  background: #f8fafc;
}

.bulk-update-preview {
  border-color: #e4e7ec !important;
  border-radius: 10px !important;
  overflow: hidden;
}
</style>
